import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  canManageUsers,
  publicFormUrl,
  type FormFolder,
  type PublicFormRow,
  type PublicFormsResponse,
} from '@jfh/intake-shared';
import { api, type Me } from '../lib/api.js';
import { byId, flattenTree, pathLabel, TOP_LEVEL_LABEL } from '../lib/folders.js';
import { ShareDialog } from '../components/ShareDialog.js';
import { Icon } from '../components/Icon.js';
import { EmptyState, PageHeader } from '../components/Primitives.js';
import { useToast } from '../components/Toast.js';

/**
 * Share forms (admins): where each form lives online — its address, whether
 * it's live, how many applications it has brought in — with Copy link and the
 * Share dialog (link, website snippets, QR code; F10 P4).
 */

const ukDate = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/London',
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});
const fmtDate = (iso: string) => ukDate.format(new Date(iso));

export function PublicForms() {
  const [me, setMe] = useState<Me | null>(null);
  const [data, setData] = useState<PublicFormsResponse | null>(null);
  const [folders, setFolders] = useState<FormFolder[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);
  const [shareFor, setShareFor] = useState<PublicFormRow | null>(null);
  const admin = me ? canManageUsers(me.role) : null;

  useEffect(() => {
    api.me().then(setMe).catch(() => setMe(null));
  }, []);

  useEffect(() => {
    if (!admin) return;
    api
      .listPublicForms()
      .then(setData)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load forms'));
    api
      .listFolders()
      .then((r) => setFolders(r.folders))
      .catch(() => undefined);
  }, [admin]);

  // Forms grouped by folder, in the same tree order as the Forms page.
  const groups = useMemo(() => {
    const index = byId(folders);
    const order = [null, ...flattenTree(folders).map((f) => f.folder.id)];
    const visible = (data?.forms ?? []).filter((f) => showArchived || !f.archivedAt);
    return order
      .map((id) => ({
        id,
        label: id ? pathLabel(index, id) : TOP_LEVEL_LABEL,
        forms: visible.filter((f) => (index.has(f.folderId ?? '') ? f.folderId : null) === id),
      }))
      .filter((g) => g.forms.length > 0);
  }, [data, folders, showArchived]);

  if (admin === false) {
    return (
      <div style={{ padding: 28 }}>
        <EmptyState icon="shield" title="Admins only" body="You don't have permission to view this page." />
      </div>
    );
  }

  const archivedCount = data?.forms.filter((f) => f.archivedAt).length ?? 0;

  return (
    <div style={{ padding: 26, maxWidth: 1100 }} className="fade-in">
      <PageHeader
        title="Share forms"
        subtitle="Each form’s web address, a website embed and a QR code for print."
        actions={
          archivedCount > 0 ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: 'var(--text-2)' }}>
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Show archived ({archivedCount})
            </label>
          ) : undefined
        }
      />

      <div
        className="card"
        style={{
          marginTop: 16,
          padding: '12px 16px',
          fontSize: 13,
          color: 'var(--text-1)',
          display: 'flex',
          gap: 10,
          alignItems: 'flex-start',
          borderLeft: '3px solid var(--st-pending)',
          background: 'var(--st-pending-soft)',
        }}
      >
        <Icon name="link" size={15} style={{ marginTop: 2, flex: 'none' }} />
        <span>
          A form’s address comes from its web address in the builder. If you change it there, every link, embed and
          QR code already shared stops working.
        </span>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, padding: 16, color: 'var(--st-declined)' }}>
          {error}
        </div>
      )}
      {!data && !error && <div style={{ marginTop: 24, color: 'var(--text-3)' }}>Loading…</div>}
      {data && groups.length === 0 && <EmptyState icon="forms" title="No forms yet" body="Create one on the Forms page." />}

      {data &&
        groups.map((g) => (
          <section key={g.id ?? 'top'} style={{ marginTop: 22 }}>
            <h2
              style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Icon name="folder" size={13} /> {g.label}
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {g.forms.map((f) => (
                <FormRow
                  key={f.id}
                  form={f}
                  baseUrl={data.baseUrl}
                  onShare={() => setShareFor(f)}
                />
              ))}
            </div>
          </section>
        ))}

      <ShareDialog
        target={shareFor ? { name: shareFor.name, slug: shareFor.slug, live: shareFor.live } : null}
        baseUrl={data?.baseUrl ?? ''}
        onClose={() => setShareFor(null)}
      />
    </div>
  );
}

function StatusBadge({ form }: { form: PublicFormRow }) {
  if (form.live) return <span className="badge approved">{form.source === 'wix' ? 'Live on Wix' : 'Live'}</span>;
  return <span className="badge neutral">{form.notLiveReason === 'archived' ? 'Archived' : 'Not published yet'}</span>;
}

function FormRow({
  form,
  baseUrl,
  onShare,
}: {
  form: PublicFormRow;
  baseUrl: string;
  onShare: () => void;
}) {
  const toast = useToast();
  const url = publicFormUrl(baseUrl, form.slug);
  const hosted = form.source !== 'wix';
  const s = form.submissions;

  const meta: string[] = [];
  if (form.version !== null && form.publishedAt) {
    meta.push(`Version ${form.version}, published ${fmtDate(form.publishedAt)}${form.publishedBy ? ` by ${form.publishedBy}` : ''}`);
  }
  if (form.archivedAt) meta.push(`Archived ${fmtDate(form.archivedAt)}`);

  return (
    <div className="card" style={{ padding: 16, opacity: form.archivedAt ? 0.75 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <Link to={`/forms/${form.id}`} style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-1)' }}>
              {form.name}
            </Link>
            <StatusBadge form={form} />
          </div>
          {meta.length > 0 && <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4 }}>{meta.join(' · ')}</div>}
        </div>
        <div style={{ display: 'flex', gap: 18, fontSize: 13 }} aria-label="Applications">
          <Stat label="Last 7 days" value={s.last7} />
          <Stat label="Last 30 days" value={s.last30} />
          <Stat label="All time" value={s.total} />
          <div style={{ color: 'var(--text-3)', fontSize: 12, alignSelf: 'flex-end', minWidth: 110 }}>
            {s.lastAt ? `Last one ${fmtDate(s.lastAt)}` : 'No applications yet'}
          </div>
        </div>
      </div>

      {hosted ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
          <code
            className="mono"
            style={{ background: 'var(--bg-sunken)', border: '1px solid var(--border)', borderRadius: 7, padding: '5px 9px', fontSize: 12.5, color: form.live ? 'var(--text-1)' : 'var(--text-3)', overflowWrap: 'anywhere' }}
          >
            {url}
          </code>
          {form.live ? (
            <>
              <button
                className="btn sm"
                onClick={() => {
                  navigator.clipboard?.writeText(url);
                  toast({ title: 'Link copied', icon: 'copy' });
                }}
              >
                <Icon name="copy" size={14} /> Copy link
              </button>
              <button className="btn sm primary" onClick={onShare}>
                <Icon name="link" size={14} /> Share…
              </button>
            </>
          ) : (
            <span style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
              {form.notLiveReason === 'archived'
                ? 'Archived: this address shows “Form not found”.'
                : 'Not published yet: this address shows “Form not found” until you publish it in the builder.'}
            </span>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 12, fontSize: 12.5, color: 'var(--text-2)' }}>
          Applications come from the Wix form on the website, so there’s no JFH Forms link to share.
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontWeight: 800, fontSize: 17, fontVariantNumeric: 'tabular-nums' }}>{value.toLocaleString('en-GB')}</div>
      <div style={{ color: 'var(--text-3)', fontSize: 11.5 }}>{label}</div>
    </div>
  );
}
